/**
 * SupportReportModal — multi-step dialog for submitting a support report.
 *
 * States:
 *   idle             — form shown (message + optional email)
 *   preparing        — calling bridge diagnostics.support.bundle_prepare
 *   submitting        — POSTing report envelope to gateway
 *   submitting-events — paginating and uploading diagnostic events
 *   done             — report submitted; shows reportId
 *   error            — shows error message with a retry option
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { submitSupportReport } from '../../services/supportReportService';
import styles from './SupportReportModal.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const IDLE = 'idle';
const PREPARING = 'preparing';
const SUBMITTING = 'submitting';
const SUBMITTING_EVENTS = 'submitting-events';
const DONE = 'done';
const ERROR = 'error';

function statusLabel(status, t) {
  switch (status) {
    case PREPARING: return t('operator.support.preparingBundle');
    case SUBMITTING: return t('operator.support.uploadingReport');
    case SUBMITTING_EVENTS: return t('operator.support.uploadingEvents');
    default: return null;
  }
}

export default function SupportReportModal({
  open,
  onClose,
  bridgeClient,
  baseUrl,
  token,
  authToken,
}) {
  const t = useLocalizedMessage();
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [status, setStatus] = useState(IDLE);
  const [reportId, setReportId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleClose = useCallback(() => {
    if (status === PREPARING || status === SUBMITTING || status === SUBMITTING_EVENTS) return;
    setMessage('');
    setContactEmail('');
    setStatus(IDLE);
    setReportId(null);
    setErrorMessage(null);
    onClose();
  }, [status, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return;

    setStatus(PREPARING);
    setErrorMessage(null);

    try {
      const result = await submitSupportReport({
        message: message.trim(),
        contactEmail: contactEmail.trim() || undefined,
        bridgeClient,
        baseUrl,
        token,
        authToken,
      });
      setReportId(result.reportId);
      setStatus(DONE);
    } catch (err) {
      setErrorMessage(err?.message || t('operator.support.submitFailed'));
      setStatus(ERROR);
    }
  }, [message, contactEmail, bridgeClient, baseUrl, token, authToken, t]);

  const isBusy = status === PREPARING || status === SUBMITTING || status === SUBMITTING_EVENTS;
  const busyLabel = statusLabel(status, t);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle><IntlMessages id="ui.home.supportReportModal.sendToSupport" /></DialogTitle>
      <DialogContent>
        {status === DONE ? (
          <div className={styles.doneBlock} data-testid="support-modal-done">
            <Typography variant="body1" gutterBottom>
              <IntlMessages id="ui.home.supportReportModal.yourReportHasBeenSubmittedShareThisIdWithSupport" />
            </Typography>
            <Typography
              variant="h6"
              className={styles.reportId}
              data-testid="support-modal-report-id"
            >
              {reportId}
            </Typography>
          </div>
        ) : (
          <>
            <TextField
              label={<IntlMessages id="ui.home.supportReportModal.describeTheIssue" />}
              multiline
              minRows={4}
              fullWidth
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isBusy}
              inputProps={{ 'data-testid': 'support-modal-message' }}
              className={styles.messageField}
            />
            <TextField
              label={<IntlMessages id="ui.home.supportReportModal.contactEmailOptional" />}
              fullWidth
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              disabled={isBusy}
              inputProps={{ 'data-testid': 'support-modal-email' }}
              className={styles.emailField}
            />
            {status === ERROR && (
              <Typography
                variant="body2"
                color="error"
                className={styles.errorText}
                data-testid="support-modal-error"
              >
                {errorMessage}
              </Typography>
            )}
            {isBusy && (
              <div className={styles.progressBlock} data-testid="support-modal-progress">
                <CircularProgress size={20} />
                {busyLabel && (
                  <Typography variant="body2" className={styles.progressLabel}>
                    {busyLabel}
                  </Typography>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {status === DONE ? (
          <Button onClick={handleClose} color="primary">
            <IntlMessages id="common.close" />
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isBusy}>
              <IntlMessages id="common.cancel" />
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={isBusy || !message.trim()}
              data-testid="support-modal-submit"
            >
              {t(isBusy ? 'operator.support.sending' : 'operator.support.sendReport')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

SupportReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bridgeClient: PropTypes.object.isRequired,
  baseUrl: PropTypes.string,
  token: PropTypes.string,
  authToken: PropTypes.string,
};

SupportReportModal.defaultProps = {
  baseUrl: null,
  token: null,
  authToken: null,
};
